import {
  type DriverActivity,
  extractApiErrorMessage,
  type Shift,
  useCostShiftMutation,
  useGetAllRateModelsQuery,
  useGetProgressDetailsQuery,
} from "@ots-frontend/ots-data";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type CostingProgress, OtsCostingProgressHost, parseCostingProgress } from "../../components/OtsCostingProgressHost";
import { useOptionalOtsToast } from "../../components/OtsToast";
import { diagnosticRunFromProblem, diagnosticRunWithFindings } from "../../diagnostics/diagnostic-runs";
import { DiagnosticsDialog } from "../../diagnostics/DiagnosticsDialog";
import type { DiagnosticRun } from "../../diagnostics/types";
import {
  type ActivityShiftCostState,
  createActionProvider,
  type DriverActivityActionProvider,
} from "../driver-activity-actions";
import {
  type ActivityRateModelOption,
  countedRateModels,
  dayRateModelOptions,
  isHaulingRateModel,
  mergeRateModelOptions,
  rateModelsNarrowed,
} from "../driver-activity-view-model";

/** How long GoBot cheers on a costed shift, and a failure stays on its row, before the button comes back. */
const DONE_FOR_MS = 2_400;
const FAILED_FOR_MS = 8_000;

export type UseDriverActivityShiftCostToolsOptions = {
  regionId?: string;
  /** The day's rows, for the ids of the rate models on its tickets. */
  rows?: DriverActivity[];
  /** The rate models picked in the workspace's display options; the hauling ones when missing. */
  rateModelNames?: string[];
  /** Admins save the costs; everyone else previews them, as in WaterGo. */
  isAdmin?: boolean;
  /** Whether this person holds a permission; everything is offered when omitted. */
  can?: (permission: string) => boolean;
};

export type DriverActivityShiftCostTools = DriverActivityActionProvider & {
  /** The region's rate models, to offer in the workspace beside those on the day's tickets. */
  rateModelOptions: ActivityRateModelOption[];
};

const regionOptionsOf = (models: { name?: string; type?: string; companyType?: string }[] | undefined): ActivityRateModelOption[] =>
  (models ?? []).flatMap((model) => model.name ? [{ name: model.name, hauling: isHaulingRateModel(model.type, model.companyType) }] : []);

const newProgressId = () => globalThis.crypto?.randomUUID?.() ?? `cost-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Follows one running costing's stages while it lasts. */
const ProgressWatcher = ({ shiftId, progressId, onProgress }: { shiftId: string; progressId: string; onProgress: (shiftId: string, progress: CostingProgress) => void }) => {
  const { data } = useGetProgressDetailsQuery(progressId ?? skipToken, { pollingInterval: 500 });
  useEffect(() => {
    const progress = parseCostingProgress(data);
    if (progress) onProgress(shiftId, progress);
  }, [data, shiftId, onProgress]);
  return null;
};

/**
 * Costing a shift, as WaterGo's Apply Shift Cost and end-of-shift money cell do: with the picked rate models,
 * saved for admins and previewed for everyone else, its stages followed while it runs. The new figures join the
 * cached day as they come back. Offered from the tools menu and each closed shift's end row.
 */
export const useDriverActivityShiftCostTools = ({
  regionId,
  rows,
  rateModelNames,
  isAdmin = false,
  can = () => true,
}: UseDriverActivityShiftCostToolsOptions = {}): DriverActivityShiftCostTools => {
  const toast = useOptionalOtsToast();
  const [costShift] = useCostShiftMutation();
  const { data: regionRateModels } = useGetAllRateModelsQuery(regionId ?? skipToken);
  const [states, setStates] = useState<Record<string, ActivityShiftCostState & { progressId?: string }>>({});
  // What costing had to say, in the diagnostics dialog, as WaterGo's DiagnosticsPresenter shows it.
  const [report, setReport] = useState<{ run: DiagnosticRun; title?: string }>();
  const running = useRef(new Set<string>());
  const timers = useRef(new Set<number>());
  useEffect(() => () => timers.current.forEach((timer) => window.clearTimeout(timer)), []);

  const later = (ms: number, then: () => void) => {
    const timer = window.setTimeout(() => {
      timers.current.delete(timer);
      then();
    }, ms);
    timers.current.add(timer);
  };
  const update = (shiftId: string, change: (current: ActivityShiftCostState | undefined) => ActivityShiftCostState) =>
    setStates((current) => ({ ...current, [shiftId]: change(current[shiftId]) }));
  const onProgress = useCallback((shiftId: string, progress: CostingProgress) =>
    setStates((current) => current[shiftId]?.status === "running" ? { ...current, [shiftId]: { ...current[shiftId], progress } } : current), []);

  // Costing takes ids; the picks are names, found among the region's rate models and those on the day's tickets.
  const idsByName = useMemo(() => {
    const ids = new Map<string, string>();
    for (const model of regionRateModels ?? []) if (model.name && model.id) ids.set(model.name, model.id);
    for (const row of rows ?? []) {
      for (const model of row.transportTransportAssoc?.objectCostRateModelList ?? []) {
        if (model.rateModelName && model.rateModelId && !ids.has(model.rateModelName)) ids.set(model.rateModelName, model.rateModelId);
      }
    }
    return ids;
  }, [regionRateModels, rows]);

  const preview = !isAdmin;
  const regionOptions = useMemo(() => regionOptionsOf(regionRateModels), [regionRateModels]);
  const allOptions = useMemo(() => mergeRateModelOptions(dayRateModelOptions(rows), regionOptions), [rows, regionOptions]);
  const run = (shift: Shift) => {
    if (running.current.has(shift.id)) return;
    const counted = countedRateModels(rateModelNames, allOptions);
    if (!counted.length) {
      toast?.show({ title: "No rate models picked", description: "Pick at least one rate model to cost the shift with.", tone: "warning" });
      return;
    }
    // Everything counted is the same as no choice, which the server takes as every rate model.
    const rateModelIds = rateModelsNarrowed(counted, allOptions) ? counted.flatMap((name) => idsByName.get(name) ?? []) : undefined;
    const progressId = newProgressId();
    running.current.add(shift.id);
    // Until this run is done, the figures shown are whatever the last one left.
    update(shift.id, (current) => ({ status: "running", startedAt: Date.now(), progressId, preview: current?.preview }));
    costShift({ shiftId: shift.id, rateModelIds, preview, progressId }).unwrap()
      .then((response) => {
        update(shift.id, () => ({ status: "done", preview }));
        const run = diagnosticRunWithFindings(response.diagnostics);
        if (run) setReport({ run });
        else if (response.warnings) toast?.show({ title: "Costed with warnings", description: response.warnings, tone: "warning" });
        later(DONE_FOR_MS, () => update(shift.id, (current) => ({ preview: current?.preview })));
      })
      .catch((failure: unknown) => {
        const error = extractApiErrorMessage(failure) ?? "The shift could not be costed.";
        update(shift.id, (current) => ({ status: "failed", error, preview: current?.preview }));
        // A failure that lists what went wrong opens the list; otherwise its message is enough.
        const run = diagnosticRunFromProblem((failure as { data?: unknown } | undefined)?.data, "COST");
        if (run) setReport({ run, title: "Shift costing failed" });
        else toast?.show({ title: "Costing failed", description: error, tone: "danger" });
        later(FAILED_FOR_MS, () => update(shift.id, (current) => ({ preview: current?.preview })));
      })
      .finally(() => running.current.delete(shift.id));
  };

  const offered = can("view_cost");
  const watchers = Object.entries(states).flatMap(([shiftId, state]) => state.status === "running" && state.progressId
    ? [<ProgressWatcher key={state.progressId} shiftId={shiftId} progressId={state.progressId} onProgress={onProgress}/>]
    : []);

  const provider = createActionProvider(
    offered ? [{
      id: "cost-shift",
      label: preview ? "Preview Shift Cost" : "Apply Shift Cost",
      scope: "single",
      group: "tools",
      isEnabled: ([entry]) => entry?.shift?.endDttm != null,
    }] : [],
    { "cost-shift": ([entry]) => entry?.shift && run(entry.shift) },
    offered || report ? <>
      {/* The progress tooltip WaterGo's money cells use, for the costing buttons on the shifts' end rows. */}
      {offered && <OtsCostingProgressHost/>}
      {watchers}
      {report && <DiagnosticsDialog run={report.run} title={report.title} onClose={() => setReport(undefined)}/>}
    </> : undefined,
  );

  return {
    ...provider,
    shiftCost: offered ? { stateOf: (shiftId) => states[shiftId], run, preview } : undefined,
    rateModelOptions: regionOptions,
  };
};
